import React, { useContext } from "react";

import {
  Button,
  Chip,
  Grid,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import { Clipboard } from "react-feather";
import { Link, useHistory } from "react-router-dom";

import { SubscriptionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { subscriptionTypeNameDisplayMapping } from "../../../constants/index";
import mainStyles from "../../../styles";
import { SearchTableBodyRowUserSearchProps } from "../../../types/search-display";
import UserOrgsCountChip from "components/shared/chip/user-orgs-count-chip";
import { SnackbarContext } from "context/snackbar-context";
import { getUserLastSeen } from "helpers/get-user-last-seen";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";

const TableBodyRowUserSearch = ({
  row,
  regionCode,
}: SearchTableBodyRowUserSearchProps) => {
  const classes = mainStyles();
  const theme = useTheme();
  const history = useHistory();

  const { copyToClipboard } = useContext(SnackbarContext);

  const userName = `${row.firstName} ${row.lastName}`;
  const userHref = `/user/${regionCode}/${row.id}`;
  const orgHref = `/organisation/${regionCode}/${row.orgId}`;

  const getSubscriptionTypeChip = () => {
    if (row.subscriptions.length === 0) {
      return (
        <Chip
          size="small"
          color="default"
          className={clsx(classes.spaceLeftXSmall, classes.spaceRightXSmall)}
          label={"Deactivated user"}
          data-testid="inactive-user-chip"
        />
      );
    }

    if (row.subscriptions.length === 1) {
      const type = row.subscriptions[0].type;
      const chipClass =
        type == SubscriptionType.FREE_ORG
          ? classes.yellowSubmarine
          : classes.purpleRain;
      return (
        <>
          <Chip
            size="small"
            color="primary"
            sx={{
              ...classes.spaceLeftXSmall,
              ...classes.spaceRightXSmall,
              ...chipClass,
            }}
            label={subscriptionTypeNameDisplayMapping[type]}
            data-testid="single-subscription-chip"
            clickable
            onClick={() => {
              if (row.orgId) {
                history.push(orgHref);
              }
            }}
          />
        </>
      );
    }

    return <></>;
  };

  const isActiveDisplay = () => (
    <span>
      {row.flags?.isActive ? " (Active User)" : " (Deactivated User)"}
    </span>
  );

  return (
    <TableRow>
      <TableCell>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                paddingY: theme.spacing(0.5),
              }}
            >
              <Link
                to={userHref}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <span style={{ marginRight: theme.spacing(0.5) }}>
                  {userName}
                </span>
              </Link>{" "}
              {getSubscriptionTypeChip()}
              <UserOrgsCountChip
                orgsCount={row.orgsCount}
                identityID={row.authId}
              />
            </Typography>

            <div onClick={() => copyToClipboard(row.email)}>
              <Typography sx={classes.clickable} variant="body1" gutterBottom>
                {row.email}{" "}
                <Clipboard
                  color={theme.palette.text.primary}
                  size={"1rem"}
                  style={{ position: "relative", top: 2 }}
                />
              </Typography>
            </div>
            <div onClick={() => copyToClipboard(row.id)}>
              <Typography sx={classes.clickable} variant="body1">
                {row.id}{" "}
                <Clipboard
                  color={theme.palette.text.primary}
                  size={"1rem"}
                  style={{ position: "relative", top: 2 }}
                />
              </Typography>
            </div>

            <Typography variant="body1" paddingTop="5px">
              <b>Organisation name: </b>
              {row.orgName}{" "}
            </Typography>
            <Typography variant="body1" paddingTop="5px">
              <b>Date registered: </b>
              {row.createdAt &&
                formatDateTimeDisplay(
                  convertTimestampToDate(row.createdAt)
                )}{" "}
              {isActiveDisplay()}
            </Typography>
            <Typography variant="body1" paddingTop="5px">
              <b>Last seen: </b>
              {getUserLastSeen(row.lastSeen)}{" "}
            </Typography>
          </Grid>

          <Grid
            item
            container
            xs={6}
            direction="column"
            justifyContent="flex-end"
          >
            <Grid
              item
              container
              direction="row"
              justifyContent="flex-end"
              spacing={2}
            >
              <Grid item>
                <Link to={userHref} className="remove-link-style">
                  <Button
                    variant="contained"
                    color="primary"
                    data-anchor="user-manage-btn"
                  >
                    Manage User
                  </Button>
                </Link>
              </Grid>

              <Grid item>
                {row.orgId && (
                  <Link to={orgHref} className="remove-link-style">
                    <Button
                      variant="contained"
                      color="primary"
                      data-testid="user-search-manage-org-btn"
                    >
                      Manage Org
                    </Button>
                  </Link>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </TableCell>
    </TableRow>
  );
};

export default TableBodyRowUserSearch;
